import React from 'react';
import { Select, Spin } from '@bedrock/components';
import _ from 'lodash';
import { v2IpResourceSelector } from '#services/api';

/** 后端 IpResourceSelectorQueryForm.name_keyword 最大长度（@Size(max=20)） */
const IP_NAME_KEYWORD_MAX_LEN = 20;

/** IP 名称中英组合展示：星门猎人（Help explain information display）；全空返回空串由调用方兜底 */
function formatIpName(name?: string, nameEn?: string): string {
  if (name && nameEn) return `${name}（${nameEn}）`;
  if (name) return name;
  if (nameEn) return nameEn;
  return '';
}

/** 选中值形态：与 formItemKeyMap { id: 'ip_id', name: 'ip_name_cn', name_en: 'ip_name_en', ip_types: 'ip_type' } 对齐 */
export interface IpResourceValue {
  id?: number;
  name?: string;
  name_en?: string;
  /** IP 类型（后端 string[]，2026-08-07 由 IpTypeEnum[] 改为 string[]，回填 form 的 ip_type） */
  ip_types?: string[];
}

interface IpResourceSelectProps {
  value?: IpResourceValue;
  onChange?: (value?: IpResourceValue) => void;
  view?: boolean;
  /** 详情回显兜底：descriptions 只读模式无 Form 绑定时展示 IP 名称 */
  ipName?: string;
  placeholder?: string;
}

/** 下拉选项：value 存 IP 资源 id，name/name_en 展示 IP 名称，ip_types 用于回填表单 */
interface IpOption {
  value: number;
  name: string;
  name_en?: string;
  /** IP 类型（后端 string[]，2026-08-07 由 IpTypeEnum[] 改为 string[]，回填 form 的 ip_type） */
  ip_types?: string[];
}

export default function IpResourceSelect({
  value,
  onChange,
  view,
  ipName,
  placeholder = '请选择',
}: IpResourceSelectProps) {
  const [options, setOptions] = React.useState<IpOption[]>([]);
  const [fetching, setFetching] = React.useState(false);
  const [fetched, setFetched] = React.useState(false);

  // 远程搜索：v2IpResourceSelector 的 name_keyword 必填（@NotBlank），空值不触发请求
  const fetchData = React.useMemo(
    () =>
      _.debounce((keyword: string) => {
        const kw = keyword.trim();
        if (!kw) return;
        setFetched(true);
        setFetching(true);
        v2IpResourceSelector({
          name_keyword: kw.slice(0, IP_NAME_KEYWORD_MAX_LEN),
        })
          .then((res) => {
            setOptions(
              (res?.data || [])
                .filter((item) => item.id != null)
                .map((item) => ({
                  value: item.id as number,
                  name: item.name || '',
                  name_en: item.name_en || '',
                  ip_types: item.ip_types,
                }))
            );
          })
          .finally(() => setFetching(false));
      }, 300),
    []
  );

  if (view) {
    return (
      <span>{formatIpName(value?.name, value?.name_en) || ipName || '--'}</span>
    );
  }

  return (
    <Select
      value={value?.id}
      className={'w-full'}
      placeholder={placeholder}
      options={options}
      showSearch
      allowClear
      filterOption={false}
      onSearch={fetchData}
      onClear={() => {
        setOptions([]);
        setFetched(false);
      }}
      onChange={(val) => {
        const option = options.find((o) => o.value === val);
        onChange?.(
          option
            ? {
                id: option.value,
                name: option.name,
                name_en: option.name_en,
                ip_types: option.ip_types,
              }
            : val != null
              ? { id: val as number }
              : undefined
        );
      }}
      renderSelectedItem={(op) => ({
        // 编辑回显时 options 未加载，优先展示 value 的中英组合（由 formItemKeyMap 从 snapshot.name/name_en 重建）；选中场景用 op 兜底
        content: (
          <span className="one-rows-ellipsis">
            {formatIpName(value?.name, value?.name_en) ||
              formatIpName(op?.name, op?.name_en)}
          </span>
        ),
        isRenderInTag: false,
      })}
      renderLabel={(op) => <span>{formatIpName(op?.name, op?.name_en)}</span>}
      notFoundContent={
        fetching ? (
          <Spin />
        ) : fetched ? (
          <div className="text-align-center pd-t-20 pd-b-20">暂无匹配IP</div>
        ) : (
          <div className="text-align-center pd-t-20 pd-b-20">
            请输入IP名称关键词搜索
          </div>
        )
      }
    />
  );
}
